import { useState, useRef, useEffect } from 'react';
import { useIsMobile } from '@/hooks/use-mobile';
interface OptimizedImageProps {
  src: string;
  alt: string;
  className?: string;
  priority?: boolean;
  quality?: number;
  loading?: 'lazy' | 'eager';
  fetchPriority?: 'high' | 'low' | 'auto';
  decoding?: 'async' | 'sync' | 'auto';
  sizes?: string;
  width?: number;
  height?: number;
}
export const OptimizedImage = ({
  src,
  alt,
  className = '',
  priority = false,
  quality = 85,
  loading = 'lazy',
  fetchPriority = 'auto',
  decoding = 'async',
  sizes,
  width,
  height
}: OptimizedImageProps) => {
  const [isLoaded, setIsLoaded] = useState(false);
  const [hasError, setHasError] = useState(false);
  const [isInView, setIsInView] = useState(priority);
  const imgRef = useRef<HTMLImageElement>(null);
  const isMobile = useIsMobile();
  useEffect(() => {
    if (priority || loading === 'eager') {
      setIsInView(true);
      return;
    }
    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) {
        setIsInView(true);
        observer.disconnect();
      }
    }, {
      threshold: 0.1,
      rootMargin: '50px'
    });
    if (imgRef.current) {
      observer.observe(imgRef.current);
    }
    return () => observer.disconnect();
  }, [priority, loading]);

  // Generate next-gen format sources
  const getWebPSrc = (originalSrc: string): string => {
    if (originalSrc.includes('/assets/') || originalSrc.includes('/lovable-uploads/')) {
      return originalSrc.replace(/\.(jpg|jpeg|png)$/i, '.webp');
    }
    return originalSrc;
  };
  const getAVIFSrc = (originalSrc: string): string => {
    if (originalSrc.includes('/assets/') || originalSrc.includes('/lovable-uploads/')) {
      return originalSrc.replace(/\.(jpg|jpeg|png)$/i, '.avif');
    }
    return originalSrc;
  };
  const optimizedSrc = src;
  const webpSrc = getWebPSrc(src);
  const avifSrc = getAVIFSrc(src);
  const hasWebPVersion = webpSrc !== src;
  const hasAVIFVersion = avifSrc !== src;

  // Generate appropriate image attributes
  const getImageAttributes = () => {
    const attributes: any = {
      src: optimizedSrc,
      alt,
      loading,
      decoding,
      onLoad: () => setIsLoaded(true),
      onError: () => setHasError(true)
    };

    // Use lowercase for DOM attribute
    if (fetchPriority !== 'auto') {
      attributes.fetchpriority = fetchPriority;
    }
    if (width) attributes.width = width;
    if (height) attributes.height = height;
    if (sizes) attributes.sizes = sizes;
    return attributes;
  };
  if (hasError) {
    return <div className={`bg-muted/20 flex items-center justify-center ${className}`}>
        
      </div>;
  }
  return <div className={`relative overflow-hidden ${className}`} ref={imgRef}>
      {!isLoaded && isInView && <div className="absolute inset-0 bg-muted/20 animate-pulse" />}
      {isInView && <>
          {hasWebPVersion || hasAVIFVersion ? <picture>
              {hasAVIFVersion && <source srcSet={avifSrc} type="image/avif" />}
              {hasWebPVersion && <source srcSet={webpSrc} type="image/webp" />}
              <img {...getImageAttributes()} className={`w-full h-full transition-opacity duration-300 ${isLoaded ? 'opacity-100' : 'opacity-0'}`} style={{
          willChange: 'auto',
          backfaceVisibility: 'hidden',
          transform: 'translateZ(0)',
          contentVisibility: isMobile ? 'auto' : undefined
        }} />
            </picture> : <img {...getImageAttributes()} className={`w-full h-full transition-opacity duration-300 ${isLoaded ? 'opacity-100' : 'opacity-0'}`} style={{
        willChange: 'auto',
        backfaceVisibility: 'hidden',
        transform: 'translateZ(0)',
        contentVisibility: isMobile ? 'auto' : undefined
      }} />}
        </>}
    </div>;
};